import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button } from '../../components/ui';
import Footer from '../../components/layout/Footer';
import catalogService from '../../services/catalogService';

const PanditProfilePage = ({ onNavigate, pandit }) => {
  const [catalogPandits, setCatalogPandits] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPandits = async () => {
      setLoadingCatalog(true);
      try {
        const data = await catalogService.getPandits();
        if (!cancelled && data.length > 0) {
          setCatalogPandits(data);
        }
      } catch (err) {
      } finally {
        if (!cancelled) {
          setLoadingCatalog(false);
        }
      }
    };

    loadPandits();

    return () => {
      cancelled = true;
    };
  }, []);

  const providerPool = catalogPandits;
  const selectedPandit = useMemo(() => {
    if (!providerPool.length && !pandit) {
      return null;
    }

    if (!pandit) {
      return providerPool[0] || null;
    }

    return providerPool.find((item) => Number(item.userId || item.id) === Number(pandit.userId || pandit.id)) || pandit;
  }, [pandit, providerPool]);

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const handleBookConsultation = () => {
    if (!onNavigate || !selectedPandit) return;

    onNavigate('booking', {
      bookingData: {
        service: {
          id: `pandit-${selectedPandit.id}`,
          title: `Consultation with ${selectedPandit.name}`,
          image: '🕉️',
          panditId: Number(selectedPandit.userId || selectedPandit.id),
          actorRole: 'pandit',
          selectedPandit
        },
        package: {
          name: 'Direct Pandit Consultation',
          price: 2500
        },
        addOns: [],
        totalPrice: 2500,
        bookingMode: 'pandit'
      }
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-orange-100">
        <div className="px-4 py-4 flex items-center">
          <button className="mr-4 p-2 hover:bg-orange-50 rounded-full transition-colors">
            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-orange-800">Pandit Profile</h1>
        </div>
      </header>

      <main className="flex-1 px-4 py-6">
        {!loadingCatalog && !selectedPandit && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <p className="text-sm text-gray-700">No pandit profile found in catalog yet.</p>
          </div>
        )}

        {selectedPandit && (
          <>
        {/* Profile Card */}
        <Card className="mb-6 bg-white border-orange-100 shadow-lg">
          <Card.Content className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center">
                <svg className="w-10 h-10 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-orange-800 mb-1">{selectedPandit.name}</h2>
                <p className="text-orange-600 text-sm mb-1">{(selectedPandit.languages || ['Hindi']).join(', ')}</p>
                <p className="text-orange-600 text-sm">{selectedPandit.location}</p>
                <p className="text-orange-800 font-medium mt-2">Experience: {selectedPandit.experienceYears}+ years</p>
                {loadingCatalog && <p className="text-xs text-gray-500 mt-2">Refreshing profile from catalog...</p>}
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* Specialties */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {(selectedPandit.specialties || []).map((specialty) => (
              <span key={specialty} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">{specialty}</span>
            ))}
          </div>
        </div>

        {/* Details Sections */}
        <div className="space-y-4 mb-6">
          <div className="bg-white rounded-xl border border-orange-100 p-4">
            <div className="flex items-center justify-between">
              <span className="text-orange-800 font-medium">Rating</span>
              <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-orange-100 p-4">
            <div className="flex items-center justify-between">
              <span className="text-orange-800 font-medium">Reviews</span>
              <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Availability Calendar */}
        <Card className="mb-6 bg-white border-orange-100">
          <Card.Content className="p-4">
            <h3 className="text-lg font-semibold text-orange-800 mb-4">Availability Calendar</h3>
            <div className="grid grid-cols-7 gap-2 mb-4">
              {weekDays.map((day, index) => (
                <div key={index} className="text-center">
                  <div className="text-sm font-medium text-orange-700 mb-2">{day}</div>
                  <div className="w-8 h-8 bg-orange-100 rounded-lg mx-auto"></div>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>

        {/* Book Consultation Button */}
        <Button
          onClick={handleBookConsultation}
          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-4 rounded-xl font-semibold shadow-lg transform hover:scale-105 transition-all duration-200"
        >
          Book Consultation
        </Button>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default PanditProfilePage;

